import type {
  ComparisonProductData,
  ComparedAttributes,
  AttributesMap,
  AttributesArray,
} from '~/types/product/ComparisonProducts';
import { DEFAULT_ATTRIBUTE_PRIORITY, MISSING_COMPARISON_VALUE } from '~/constants/comparisonProducts';

const getInitialValues = (productsDataToCompare: ComparisonProductData[]): ComparedAttributes => {
  const attributes: ComparedAttributes = {};
  productsDataToCompare.forEach(productData => {
    const productSku = productData.product.sku;
    if (productSku) {
      attributes[productSku] = [MISSING_COMPARISON_VALUE];
    }
  });
  return attributes;
};

const sortAttributesAlphabeticallyWithPriority = (attributesArray: AttributesArray): void => {
  attributesArray.sort((attributeA, attributeB) => {
    const priorityCheck = attributeB.priority - attributeA.priority;
    if (priorityCheck !== 0) {
      return priorityCheck;
    }
    return attributeA.key.localeCompare(attributeB.key);
  });
};

const parseProductsIntoAttributes = (productsDataToCompare: ComparisonProductData[]): AttributesArray => {
  const attributes: AttributesMap = new Map();
  const initialValues = getInitialValues(productsDataToCompare);

  productsDataToCompare.forEach(productData => {
    const productSku = productData.product.sku;
    if (!productSku) {
      return;
    }

    const technicalAttributes = productData.attributesLocalized.techdetails || [];
    technicalAttributes.forEach(attribute => {
      const attributeKey = attribute.key;

      if (!attributes.has(attributeKey)) {
        attributes.set(attributeKey, {
          values: { ...initialValues },
          priority: attribute.priority || DEFAULT_ATTRIBUTE_PRIORITY,
        });
      }

      const attributeEntry = attributes.get(attributeKey);
      if (attributeEntry) {
        attributeEntry.values[productSku] = attribute.values || [MISSING_COMPARISON_VALUE];
        attributes.set(attributeKey, attributeEntry);
      }
    });
  });

  return Array.from(attributes, ([key, attributeData]) => ({
    key,
    values: attributeData.values,
    priority: attributeData.priority,
  }));
};

export const getAttributesToCompare = (productsDataToCompare: ComparisonProductData[]): AttributesArray => {
  const attributes = parseProductsIntoAttributes(productsDataToCompare);
  sortAttributesAlphabeticallyWithPriority(attributes);

  return attributes;
};
